// ============================================================================
// NotificationSettingsModal - per-user push notification preferences
// ============================================================================
// Writes users/{uid}/settings/notifications - the exact doc the server's
// sendPushNotification consults before every send (master toggle, per-type
// keys, quiet hours). Keys must match TYPE_TO_PREF_KEY server-side; a typo
// here silently re-enables a muted category. Toggles write through on
// change - no save button to forget.
// ============================================================================
import React, { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  VStack, HStack, Text, Switch, Divider, Select, Spinner, Box, Button,
} from '@chakra-ui/react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../../services/firebase';
import { pushNotificationService } from '../../services/pushNotificationService';

interface Props { isOpen: boolean; onClose: () => void; }

const TYPES: Array<{ key: string; label: string; blurb: string }> = [
  { key: 'gameAlerts',     label: 'Game alerts',      blurb: 'Table events while you play' },
  { key: 'turnAlerts',     label: 'Turn alerts',      blurb: 'Push when it is your turn and the app is in the background' },
  { key: 'tableInvites',   label: 'Table invites',    blurb: 'When a friend invites you to a game' },
  { key: 'directMessages', label: 'Direct messages',  blurb: 'New DMs from friends' },
  { key: 'mentions',       label: 'Mentions',         blurb: 'When someone @mentions you' },
  { key: 'announcements',  label: 'Announcements',    blurb: 'Platform news and updates' },
  { key: 'promotions',     label: 'Promotions',       blurb: 'Pack Drops, bonuses, special offers' },
];

const HOURS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`);

const NotificationSettingsModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const [prefs, setPrefs] = useState<any | null>(null);
  // "Enable PSS Notifications" - the recovery button for devices whose
  // startup token registration silently failed (toggle on, no token, no
  // pushes). Runs the full permission/register/save chain and reports the
  // outcome right here in the modal.
  const [enabling, setEnabling] = useState(false);
  const [enableResult, setEnableResult] = useState<{ ok: boolean; message: string } | null>(null);
  const handleEnablePush = async () => {
    setEnabling(true);
    setEnableResult(null);
    try {
      setEnableResult(await pushNotificationService.enablePush());
    } catch (e: any) {
      setEnableResult({ ok: false, message: e?.message || 'Something went wrong. Please try again.' });
    } finally {
      setEnabling(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    let cancelled = false;
    getDoc(doc(db, 'users', uid, 'settings', 'notifications')).then(snap => {
      if (!cancelled) setPrefs(snap.exists() ? snap.data() : {});
    }).catch(() => { if (!cancelled) setPrefs({}); });
    return () => { cancelled = true; };
  }, [isOpen]);

  const save = (patch: Record<string, any>) => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    setPrefs((p: any) => ({ ...(p || {}), ...patch }));
    setDoc(doc(db, 'users', uid, 'settings', 'notifications'), patch, { merge: true })
      .catch(err => console.error('[NOTIF_PREFS] save failed:', err));
  };

  // Absent key = enabled, matching the server's `=== false` checks.
  // turnAlerts is OPT-IN (off until explicitly enabled); everything else
  // defaults on (absent = enabled)
  const on = (key: string) => key === 'turnAlerts'
    ? prefs?.turnAlerts === true
    : prefs?.[key] !== false;

  const masterOn = on('enabled');

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="#1C1C1E" color="white" mx={4}>
        <ModalHeader fontSize="md">Notifications</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {prefs === null ? (
            <Box textAlign="center" py={8}><Spinner color="cyan.400" /></Box>
          ) : (
            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between">
                <Box>
                  <Text fontSize="sm" fontWeight="600">Push notifications</Text>
                  <Text fontSize="xs" color="gray.500">Master switch for everything below</Text>
                </Box>
                <Switch colorScheme="cyan" isChecked={masterOn}
                  onChange={e => save({ enabled: e.target.checked })} />
              </HStack>

              <Box>
                <Button size="sm" w="100%" colorScheme="cyan" variant="outline"
                  isLoading={enabling} loadingText="Connecting this device"
                  onClick={handleEnablePush}>
                  Enable PSS Notifications
                </Button>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Not getting pushes? This connects this device to PSS notifications.
                </Text>
                {enableResult && (
                  <Text fontSize="xs" mt={1.5}
                    color={enableResult.ok ? 'green.300' : 'orange.300'}>
                    {enableResult.message}
                  </Text>
                )}
              </Box>

              <Divider borderColor="whiteAlpha.200" />

              {TYPES.map(t => (
                <HStack key={t.key} justify="space-between" opacity={masterOn ? 1 : 0.4}>
                  <Box>
                    <Text fontSize="sm">{t.label}</Text>
                    <Text fontSize="xs" color="gray.500">{t.blurb}</Text>
                  </Box>
                  <Switch colorScheme="cyan" isChecked={on(t.key)} isDisabled={!masterOn}
                    onChange={e => save({ [t.key]: e.target.checked })} />
                </HStack>
              ))}

              <Divider borderColor="whiteAlpha.200" />

              <HStack justify="space-between" opacity={masterOn ? 1 : 0.4}>
                <Box>
                  <Text fontSize="sm">Quiet hours</Text>
                  <Text fontSize="xs" color="gray.500">No pushes during this window</Text>
                </Box>
                <Switch colorScheme="cyan" isChecked={prefs?.quietHoursEnabled === true}
                  isDisabled={!masterOn}
                  onChange={e => save({ quietHoursEnabled: e.target.checked })} />
              </HStack>
              {prefs?.quietHoursEnabled === true && masterOn && (
                <HStack spacing={3}>
                  <Select size="sm" bg="gray.800" borderColor="gray.600" w="110px"
                    value={prefs?.quietHoursStart || '22:00'}
                    onChange={e => save({ quietHoursStart: e.target.value })}>
                    {HOURS.map(h => <option key={h} value={h} style={{ background: '#1A202C' }}>{h}</option>)}
                  </Select>
                  <Text fontSize="xs" color="gray.500">to</Text>
                  <Select size="sm" bg="gray.800" borderColor="gray.600" w="110px"
                    value={prefs?.quietHoursEnd || '08:00'}
                    onChange={e => save({ quietHoursEnd: e.target.value })}>
                    {HOURS.map(h => <option key={h} value={h} style={{ background: '#1A202C' }}>{h}</option>)}
                  </Select>
                </HStack>
              )}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default NotificationSettingsModal;
